import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import { getMediaOptions, useProjectMedia } from '@/api/media';
import { closeModal, openModal } from '@/components/modals/modals.utils';
import type { UploadMediaModalType } from '@/features/estate-media/components/upload-media-modal';
import {
  ACCEPTED_FLOORPLANS_INPUT_TYPE,
  uploadLayoutsSchema,
} from '@/features/estate-media/schema/upload-media';
import { useDeleteFloorLayouts } from '@/features/project-media/api/delete-floor-layouts';
import { useUploadFloorLayouts } from '@/features/project-media/api/upload-floor-layout';

import { MediaData } from './media-data';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout/media/floor-plans'
);

export const FloorPlansDashboard = () => {
  const { projectId } = route.useParams();

  const { mutateAsync: uploadFloorLayouts } = useUploadFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
      },
    },
  });

  const { mutateAsync: deleteFloorLayouts } = useDeleteFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
      },
    },
  });

  const submitHandler = async (data: UploadMediaModalType) => {
    // Convert File[] to FileList
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadFloorLayouts({ layouts: dataTransfer.files, projectId });
    closeModal();
  };

  const handleOpenModal = () => {
    openModal('upload-media', {
      submitHandler,
      acceptedType: ACCEPTED_FLOORPLANS_INPUT_TYPE,
      acceptedMediaSchema: uploadLayoutsSchema(),
    });
  };

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const handleDelete = (selectedIds: string[], callback = () => {}) => {
    openModal('confirmation-modal', {
      title: `${t('delete')} ${t('image')} `,
      description: t('confirmDeleteDocument'),
      confirmationText: t('delete'),
      onSubmit: async () => {
        await deleteFloorLayouts({ projectId, layoutIds: selectedIds });
        callback();
      },
    });
  };

  const { data } = useProjectMedia({ id: projectId });

  return (
    <MediaData
      data={data.layouts || []}
      onUpload={handleOpenModal}
      handleDelete={handleDelete}
      page="floor-plans"
      type="project"
      projectId={projectId}
      isThumbnailDisabled
    />
  );
};
